import {
  Typography,
  Tooltip,
  Modal,
  SvgIcon,
  SvgNames,
} from "@sunrun/experience-ui-components";
import styled from "@emotion/styled/macro";
import { ReactNode, useState } from "react";
import infoIconGray from "../assets/images/info-icon-gray.svg";
import {
  ModalButton,
  // eslint-disable-next-line import/no-restricted-paths
} from "./GlobalStyles";
import { formatCurrency } from "utils/formatHelpers";

type RightColProps = {
  cost: number | string;
  decimals?: number;
  unit?: string;
  maxCost?: number | string;
  accentColor?: string;
  valueMedium?: boolean;
  valueBold?: boolean;
  underline?: string;
};
type RowContainerProps = RightColProps & {
  label: string;
  bold?: boolean;
  medium?: boolean;
  accentColor?: string;
};
type RowContainerWithTooltipProps = RowContainerProps & {
  description?: string;
  tooltip?: string;
};
type RowContainerWithModalProps = RowContainerProps & {
  modalDescription: ReactNode;
  ariaLabel: string;
};

const RightCol = ({
  cost,
  decimals = 0,
  unit,
  maxCost,
  accentColor,
  valueMedium,
  valueBold,
  underline,
}: RightColProps) => (
  <div
    style={{ display: "flex", flexDirection: "column", alignItems: "flex-end" }}
  >
    <StyledRightCol>
      <StyledNumberField
        accentColor={accentColor}
        valueMedium={valueMedium}
        valueBold={valueBold}
      >
        {typeof cost === "string" ? cost : formatCurrency(cost, decimals)}&nbsp;
      </StyledNumberField>
      {!maxCost && unit && (
        <AccentSpan
          accentColor={accentColor}
          valueMedium={valueMedium}
          valueBold={valueBold}
        >
          {unit}&nbsp;
        </AccentSpan>
      )}
      {maxCost && (
        <AccentDiv
          accentColor={accentColor}
          valueMedium={valueMedium}
          valueBold={valueBold}
        >
          up to &nbsp;
          {typeof maxCost === "string"
            ? maxCost
            : formatCurrency(maxCost, decimals)}
          &nbsp;
          {unit && <span>{unit}</span>}
        </AccentDiv>
      )}
    </StyledRightCol>
    {underline && (
      <Typography
        size={12}
        style={{
          fontStyle: "italic",
          textAlign: "right",
        }}
      >
        {underline}
      </Typography>
    )}
  </div>
);

const RowContainer = ({
  label,
  bold,
  medium,
  accentColor,
  ...otherProps
}: RowContainerProps) => (
  <StyledRowContainer>
    <div>
      <AccentSpan
        className={bold ? "bold" : medium ? "medium" : ""}
        accentColor={accentColor}
      >
        {label}&nbsp;
      </AccentSpan>
    </div>

    <RightCol
      {...otherProps}
      accentColor={accentColor}
      valueMedium={medium}
      valueBold={bold}
    />
  </StyledRowContainer>
);

const RowContainerWithTooltip = ({
  tooltip,
  label,
  description,
  accentColor,
  bold,
  medium,
  ...otherProps
}: RowContainerWithTooltipProps) => (
  <StyledRowContainer>
    <FlexGrowDiv>
      <Tooltip content={tooltip}>
        <AccentSpan
          className={bold ? "bold" : medium ? "medium" : ""}
          accentColor={accentColor}
        >
          {label}&nbsp;
        </AccentSpan>
        {accentColor ? (
          <SvgIcon
            name={SvgNames.InfoCircle}
            width={14}
            height={14}
            color={accentColor}
          />
        ) : (
          <img alt="info icon tooltip" src={infoIconGray}></img>
        )}
      </Tooltip>
      {description && (
        <Typography size="small" color={accentColor} as="span">
          {description}
        </Typography>
      )}
    </FlexGrowDiv>

    <RightCol
      {...otherProps}
      accentColor={accentColor}
      valueMedium={medium}
      valueBold={bold}
    />
  </StyledRowContainer>
);

const RowContainerWithModal = ({
  modalDescription,
  label,
  ariaLabel,
  accentColor,
  medium,
  ...otherProps
}: RowContainerWithModalProps) => {
  const [showModal, setShowModal] = useState(false);

  return (
    <>
      {showModal && (
        <Modal
          title={label}
          onClose={() => setShowModal(false)}
          testId="price-globals-modal"
        >
          <Typography as="p">{modalDescription}</Typography>
        </Modal>
      )}

      <StyledRowContainer>
        <div>
          <AccentSpan accentColor={accentColor}>{label}&nbsp;</AccentSpan>
          <ModalButton
            onClick={() => setShowModal(true)}
            aria-label={ariaLabel}
          >
            {accentColor ? (
              <SvgIcon
                name={SvgNames.InfoCircle}
                width={14}
                height={14}
                color={accentColor}
              />
            ) : (
              <img alt="info icon tooltip" src={infoIconGray}></img>
            )}
          </ModalButton>
        </div>

        <RightCol
          {...otherProps}
          accentColor={accentColor}
          valueMedium={medium}
        />
      </StyledRowContainer>
    </>
  );
};

type AccentProps = {
  accentColor?: string;
  valueMedium?: boolean;
  valueBold?: boolean;
};

const StyledNumberField = styled.span<AccentProps>`
  display: block;
  font-size: 20px;
  font-style: bold;
  font-weight: ${(p) => (p.valueMedium ? 500 : p.valueBold ? 600 : 400)};
  letter-spacing: -0.4px;
  color: ${(p) => p.accentColor ?? "inherit"};
`;

const AccentSpan = styled.span<AccentProps>`
  color: ${(p) => p.accentColor ?? "inherit"};
  font-weight: ${(p) => (p.valueMedium ? 500 : p.valueBold ? 600 : "inherit")};
`;

const AccentDiv = styled.div<AccentProps>`
  color: ${(p) => p.accentColor ?? "inherit"};
  font-weight: ${(p) => (p.valueMedium ? 500 : p.valueBold ? 600 : "inherit")};
`;

const FlexGrowDiv = styled.div`
  flex-grow: 1;
`;

const StyledRowContainer = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 16px;
  margin: 16px 0;
  & .bold {
    font-weight: 600;
    font-size: 1.1rem;
  }
  & .medium {
    font-weight: 500;
    font-size: 1.1rem;
  }
`;

const StyledRightCol = styled.div`
  text-align: right;
  display: flex;
  flex-direction: row;
  align-items: flex-end;
`;

export {
  RightCol,
  RowContainer,
  RowContainerWithTooltip,
  RowContainerWithModal,
};
